import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";

import useSkeleton from "../../../../hooks/useSkeleton";

import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { classNames } from "primereact/utils";

import "react-toastify/dist/ReactToastify.css";

import useCommonState from "../../../../hooks/useCommonState";

const BeneficiariesForm = ({ settings }) => {
  const { isLoading, setIsLoading, setToastConfig, t } = useCommonState();

  const s = useSkeleton("abm");

  const isEditMode = settings.formMode === "UPDATE";
  const isDeleteMode = settings.formMode === "DELETE";

  const {
    unregister,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      lastName: "",
      documentType: 1,
      documentNumber: "",
      phone: "",
      address: "",
      notes: "",
      active: 0,
    },
  });

  const [isVisible, setIsVisible] = useState(!(isEditMode || isDeleteMode));

  const formModeConfig = {};

  if (isEditMode) {
    formModeConfig.SubmitButtonText = t("buttons.update");
    formModeConfig.SubmitButtonTextWait = `${t("buttons.updating")}...`;
    formModeConfig.serverAction = async (data) => {
      await settings.CRUDService.updateEntity(data);
    };
  } else if (isDeleteMode) {
    formModeConfig.SubmitButtonText = t("buttons.delete");
    formModeConfig.SubmitButtonTextWait = `${t("buttons.deleting")}...`;
    formModeConfig.serverAction = async () => {
      await settings.CRUDService.deleteEntity(settings.entityId);
    };
  } else {
    formModeConfig.SubmitButtonText = t("buttons.create");
    formModeConfig.SubmitButtonTextWait = `${t("buttons.creating")}...`;
    formModeConfig.serverAction = async (data) => {
      await settings.CRUDService.createEntity(data);
    };
  }

  useEffect(() => {
    if (isEditMode || isDeleteMode) {
      const fetchData = async () => {
        try {
          const data = await settings.CRUDService.getEntity(settings.entityId);

          for (const key in data) {
            setValue(key, data[key]);
          }
        } catch (error) {
          console.error(t("forms.messages.operation.error"), error);
        }
      };

      fetchData();
    }
  }, [settings.entityId, setValue, settings.formMode]);

  const handleShow = (e) => {
    e.preventDefault();
    if (isVisible) {
      unregister("password");
    }
    setIsVisible(!isVisible);
  };

  const handleHide = () => {
    setIsVisible(false);
  };

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      await formModeConfig.serverAction(data);

      settings.hideDialog();

      setToastConfig({
        severity: "success",
        summary: t("forms.messages.titles.success"),
        detail: t("forms.messages.operation.success"),
        life: 3000,
        visible: true,
      });
    } catch (error) {
      let summary = t("forms.messages.titles.error");
      let detail = t("forms.messages.operation.error");

      if (error.response.data == "USERS_EXCEPTION__DUPLICATED_MAIL") {
        summary = t("errors.duplicatedMail.title");
        detail = t("errors.duplicatedMail.message");
      }
      setToastConfig({
        severity: "error",
        summary,
        detail,
        life: 3000,
        visible: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getFormErrorMessage = (error) => {
    return error && <small className="p-error">{error.message}</small>;
  };

  return (
    <React.Fragment>
      <div className={s.get()}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={`${s.get("form")} ${settings.formMode !== "DELETE" ? "abm-create-form" : ""}`}
        >
          <Controller
            name="name"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.beneficiaries.name"),
              }),
              maxLength: {
                value: 50,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.beneficiaries.name"),
                  maxLength: 50,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="name">
                    {t("entities.beneficiaries.name")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.name)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="name"
                      {...field}
                      value={field.value ?? ""}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      disabled={settings.formMode == "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="lastName"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.beneficiaries.lastName"),
              }),
              maxLength: {
                value: 50,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.beneficiaries.lastName"),
                  maxLength: 50,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="lastName">
                    {t("entities.beneficiaries.lastName")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.lastName)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="lastName"
                      {...field}
                      value={field.value ?? ""}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      disabled={settings.formMode == "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="documentNumber"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.beneficiaries.documentNumber"),
              }),
              maxLength: {
                value: 50,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.beneficiaries.documentNumber"),
                  maxLength: 50,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="documentNumber">
                    {t("entities.beneficiaries.documentNumber")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.documentNumber)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="documentNumber"
                      {...field}
                      value={field.value ?? ""}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      disabled={settings.formMode == "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="phone"
            control={control}
            rules={{
              maxLength: {
                value: 50,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.beneficiaries.phone"),
                  maxLength: 50,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="phone">
                    {t("entities.beneficiaries.phone")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.phone)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="phone"
                      {...field}
                      value={field.value ?? ""}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      disabled={settings.formMode == "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="address"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.beneficiaries.address"),
              }),
              maxLength: {
                value: 500,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.beneficiaries.address"),
                  maxLength: 500,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="address">
                    {t("entities.beneficiaries.address")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.address)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="address"
                      {...field}
                      value={field.value ?? ""}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      disabled={settings.formMode == "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />

          <Controller
            name="email"
            control={control}
            rules={
              settings.formMode == "DELETE"
                ? ""
                : {
                  pattern: {
                    value: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
                    message: t("forms.validation.noValidEmail"),
                  },
                  maxLength: {
                    value: 60,
                    message: t("forms.validation.maxLength", {
                      entity: t("entities.beneficiaries.email"),
                      maxLength: 60,
                    }),
                  },
                }
            }
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="email">
                    {t("entities.beneficiaries.email")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.email)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="email"
                      {...field}
                      value={field.value || ""}
                      maxLength={60}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      autoComplete="off"
                      disabled={settings.formMode === "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />

          <Controller
            name="nick"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.beneficiaries.nick"),
              }),
              maxLength: {
                value: 500,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.beneficiaries.nick"),
                  maxLength: 500,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="nick">
                    {t("entities.beneficiaries.nick")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.nick)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="nick"
                      {...field}
                      value={field.value ?? ""}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      disabled={settings.formMode == "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />

          <Controller
            name="password"
            control={control}
            rules={
              isVisible
                ? {
                  required: t("forms.validation.required", {
                    entity: t("entities.beneficiaries.password"),
                  }),
                  maxLength: {
                    value: 500,
                    message: t("forms.validation.maxLength", {
                      entity: t("entities.beneficiaries.password"),
                      maxLength: 500,
                    }),
                  },
                }
                : ""
            }
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="password">
                    {t("entities.beneficiaries.password")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.password)}
                  </div>
                  <div className="flex flex-row">
                    {isVisible && (
                      <Password
                        id="password"
                        {...field}
                        inputRef={field.ref}
                        className={[
                          "form-control",
                          classNames({
                            "p-invalid": fieldState.invalid,
                          }),
                        ]}
                        maxLength={100}
                        feedback={false}
                        disabled={settings.formMode === "DELETE"}
                        autoComplete="new-password"
                      />
                    )}
                    {isEditMode && (
                      <Button
                        style={{ boxShadow: "none" }}
                        label={
                          isVisible
                            ? t("entities.beneficiaries.hidePassword")
                            : t("entities.beneficiaries.showPassword")
                        }
                        onClick={handleShow}
                        link
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="notes"
            control={control}
            rules={{
              maxLength: {
                value: 500,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.beneficiaries.notes"),
                  maxLength: 500,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="notes">
                    {t("entities.beneficiaries.notes")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.notes)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="notes"
                      {...field}
                      value={field.value ?? ""}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      disabled={settings.formMode == "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="active"
            control={control}
            rules={{}}
            render={({ field }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="active">
                    {t("entities.beneficiaries.active")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.active)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <Checkbox
                      inputId="active"
                      onChange={(e) => field.onChange(e.checked)}
                      checked={field.value}
                      disabled={settings.formMode == "DELETE"}
                      className="flex mt-2"
                    />
                  </div>
                </div>
              </div>
            )}
          />

          <div className="flex justify-content-between mt-6 mb-2">
            <Button
              label={t("buttons.cancel")}
              icon="pi pi-times"
              className="p-button-text btn-secondary-crud"
              onClick={() => settings.hideDialog("CANCEL")}
              type="button"
              disabled={isLoading}
            />
            <Button
              label={
                isLoading
                  ? formModeConfig.SubmitButtonTextWait
                  : formModeConfig.SubmitButtonText
              }
              icon="pi pi-check"
              className=" btn btn-primary btn-primary-crud"
              type="submit"
              disabled={isLoading}
            />
          </div>
        </form>
      </div>
    </React.Fragment>
  );
};

export default BeneficiariesForm;
